import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { config } from '../../../../config/envs';
import { useImpersonate } from '../../../hooks/useImpersonate';
import { IMPERSONATE_TEXTS } from '../../../constants/impersonate';
import Disable2FAModal from './Disable2FAModal';
import { JdsButton } from '../../../components/jds/JdsButton';
import { JdsTag } from '../../../components/jds/JdsTag';
import { JdsTableSkeleton } from '../../../components/jds/JdsSkeleton';
import { MoreIcon } from '../../../components/jds/icons';
import '../../../components/jds/jds.css';
import '../clienteDados.css';

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
}

function stopMouseDownWhenOpen(event) {
  if (event.currentTarget.getAttribute('aria-expanded') === 'true') {
    event.stopPropagation();
  }
}

function StatusBadge({
  hasTwoFactorEnabled,
  isTwoFactorDeactivated,
  reason,
  userId,
}) {
  let badge;
  let tooltipText = null;

  if (!hasTwoFactorEnabled) {
    badge = (
      <span>
        <JdsTag color="neutral" label="Não configurado" />
      </span>
    );
    tooltipText = '2FA nunca foi configurado';
  } else if (isTwoFactorDeactivated) {
    badge = (
      <span>
        <JdsTag color="error" label="Temporariamente desativado" />
      </span>
    );
    if (reason) {
      tooltipText = `Motivo: ${reason}`;
    }
  } else {
    badge = (
      <span>
        <JdsTag color="success" label="Habilitado" />
      </span>
    );
  }

  if (tooltipText) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`tooltip-status-${userId}`}>{tooltipText}</Tooltip>
        }
      >
        {badge}
      </OverlayTrigger>
    );
  }

  return badge;
}

export default function AccountSecurity({ clientId }) {
  const [loading, setLoading] = useState(false);
  const [usersSecurity, setUsersSecurity] = useState([]);
  const [rowLoading, setRowLoading] = useState(null);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { impersonate } = useImpersonate();

  const loadUsersSecurity = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${config.app.adminBaseUrl}/account/${clientId}/security-info`,
      );
      setUsersSecurity(data);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        'Não foi possível carregar os status de 2FA.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsersSecurity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const toggleTwoFactor = async (user, reason) => {
    if (!user || !user.hasTwoFactorEnabled) return;
    setRowLoading(user.id);
    try {
      const trimmedReason = reason ? reason.trim() : '';
      const path = user.isTwoFactorDeactivated
        ? `/account/${user.id}/enable-two-factor`
        : `/account/${user.id}/disable-two-factor`;

      const { data } = await axios.post(`${config.app.adminBaseUrl}${path}`, {
        reason: trimmedReason,
      });

      if (data.message) {
        toast.success(data.message);
      }

      await loadUsersSecurity();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        'Não foi possível alternar o status do 2FA.';
      toast.error(msg);
    } finally {
      setRowLoading(null);
    }
  };

  const handleActionClick = (user) => {
    if (user.isTwoFactorDeactivated) {
      toggleTwoFactor(user);
    } else {
      setSelectedUser(user);
      setShowDisableModal(true);
    }
  };

  const handleImpersonateClick = async (user) => {
    setRowLoading(user.id);
    await impersonate({ userId: user.id });
    setRowLoading(null);
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between mb-6">
            <div>
              <h2 className="jds-title-md mb-1">Segurança da conta</h2>
              <span className="text-muted">
                Visualização de 2FA do titular e subusuários.
              </span>
            </div>
          </div>

          {loading ? (
            <JdsTableSkeleton rows={4} columns={5} />
          ) : (
            <div>
              <h3 className="jds-title-sm mb-4">
                Usuários e status de autenticação de dois fatores
              </h3>
              <div>
                <div className="table-responsive">
                  <table className="table jds-table mb-0">
                    <thead>
                      <tr>
                        <th>Usuário</th>
                        <th>E-mail</th>
                        <th>Status 2FA</th>
                        <th>Expira em</th>
                        <th className="text-right">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersSecurity.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center py-6 text-muted"
                          >
                            Nenhum usuário encontrado.
                          </td>
                        </tr>
                      )}
                      {usersSecurity.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center">
                                {user.isPrimary && (
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip
                                        id={`tooltip-primary-${user.id}`}
                                      >
                                        Esse usuário é o titular
                                      </Tooltip>
                                    }
                                  >
                                    <span className="mr-2">
                                      <JdsTag color="brand" label="Titular" />
                                    </span>
                                  </OverlayTrigger>
                                )}
                                <span
                                  style={{ fontWeight: 'var(--fw-medium)' }}
                                >
                                  {user.name}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>
                            <StatusBadge
                              hasTwoFactorEnabled={user.hasTwoFactorEnabled}
                              isTwoFactorDeactivated={
                                user.isTwoFactorDeactivated
                              }
                              reason={user.reason}
                              userId={user.id}
                            />
                          </td>
                          <td className="text-muted">
                            {user.hasTwoFactorEnabled &&
                            user.isTwoFactorDeactivated &&
                            user.expires_at
                              ? formatDateTime(user.expires_at)
                              : '-'}
                          </td>
                          <td className="text-right">
                            <Dropdown alignRight>
                              <Dropdown.Toggle
                                as={JdsButton}
                                id={`account-security-actions-${user.id}`}
                                variant="ghost"
                                color="neutral"
                                size="sm"
                                iconOnly
                                aria-label="Ações do usuário"
                                disabled={rowLoading === user.id}
                                onMouseDown={stopMouseDownWhenOpen}
                              >
                                {rowLoading === user.id ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                ) : (
                                  <MoreIcon />
                                )}
                              </Dropdown.Toggle>
                              {createPortal(
                                <Dropdown.Menu
                                  className="jds-scope dropdown-menu-sm"
                                  rootCloseEvent="mousedown"
                                >
                                  <Dropdown.Item
                                    onClick={() => handleImpersonateClick(user)}
                                    disabled={rowLoading === user.id}
                                    style={{ whiteSpace: 'normal' }}
                                  >
                                    {IMPERSONATE_TEXTS.MENU_ITEM_LABEL}
                                  </Dropdown.Item>
                                  {user.hasTwoFactorEnabled && (
                                    <Dropdown.Item
                                      onClick={() => handleActionClick(user)}
                                      disabled={rowLoading === user.id}
                                      style={{ whiteSpace: 'normal' }}
                                    >
                                      {user.isTwoFactorDeactivated
                                        ? 'Reativar 2FA'
                                        : 'Desativar 2FA temporariamente'}
                                    </Dropdown.Item>
                                  )}
                                </Dropdown.Menu>,
                                document.body,
                              )}
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Disable2FAModal
        show={showDisableModal}
        onHide={() => setShowDisableModal(false)}
        onConfirm={async (reason) => {
          if (selectedUser) {
            await toggleTwoFactor(selectedUser, reason);
          }
          setShowDisableModal(false);
        }}
        user={selectedUser}
        loading={rowLoading === selectedUser?.id}
      />
    </>
  );
}
